import {
  Apple,
  ArrowDownToLine,
  ArrowRight,
  Check,
  ChevronDown,
  CircleHelp,
  ExternalLink,
  FileCheck2,
  Globe2,
  Mail,
  Menu,
  Play,
  ShieldCheck,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { legalContent, supportedCountries } from './content';

type LegalPageName = keyof typeof legalContent;

const googlePlayUrl = 'https://play.google.com/store/apps/details?id=com.entrfill';
const appleAppStoreUrl = 'https://apps.apple.com/cn/app/entrfill/id6797805393';

const homeLinks = [
  { href: '#workflow', label: 'How it works' },
  { href: '#product', label: 'What it fills' },
  { href: '#countries', label: 'Countries' },
  { href: '#safety', label: 'Safety' },
];

function Brand() {
  return (
    <a className="brand" href="/" aria-label="Entrfill home">
      <img src="/entrfill/brand-mark.png" alt="" />
      <span>Entrfill</span>
    </a>
  );
}

function SiteHeader({ isHome = false }: { isHome?: boolean }) {
  const [isOpen, setIsOpen] = useState(false);
  const downloadHref = isHome ? '#availability' : '/#availability';
  const links = isHome
    ? homeLinks
    : [
        { href: '/', label: 'Home' },
        ...homeLinks.map((link) => ({ ...link, href: `/${link.href}` })),
      ];

  return (
    <header className="site-header">
      <div className="site-header__inner">
        <Brand />
        <nav className="site-nav" aria-label="Primary navigation">
          {links.map((link) => (
            <a href={link.href} key={link.href}>
              {link.label}
            </a>
          ))}
          <a className="site-nav__support" href="/support.html">
            Support
          </a>
        </nav>
        <a className="header-cta" href={downloadHref}>
          <ArrowDownToLine aria-hidden="true" size={15} strokeWidth={1.8} />
          Download
        </a>
        <a
          aria-label="Download Entrfill"
          className="mobile-download-cta"
          href={downloadHref}
        >
          <ArrowDownToLine aria-hidden="true" size={16} strokeWidth={1.9} />
          <span>Download</span>
        </a>
        <button
          aria-expanded={isOpen}
          aria-label={isOpen ? 'Close navigation' : 'Open navigation'}
          className="menu-toggle"
          onClick={() => setIsOpen((open) => !open)}
          type="button"
        >
          {isOpen ? <X aria-hidden="true" size={20} /> : <Menu aria-hidden="true" size={20} />}
        </button>
      </div>
      {isOpen ? (
        <nav className="mobile-nav" aria-label="Mobile navigation">
          {links.map((link) => (
            <a href={link.href} key={link.href} onClick={() => setIsOpen(false)}>
              {link.label}
            </a>
          ))}
          <a href="/support.html" onClick={() => setIsOpen(false)}>
            Support
          </a>
          <a
            href={appleAppStoreUrl}
            onClick={() => setIsOpen(false)}
            rel="noreferrer"
            target="_blank"
          >
            Download on the App Store
          </a>
          <a
            href={googlePlayUrl}
            onClick={() => setIsOpen(false)}
            rel="noreferrer"
            target="_blank"
          >
            Download on Google Play
          </a>
        </nav>
      ) : null}
    </header>
  );
}

function SiteFooter() {
  return (
    <footer className="site-footer">
      <div className="site-footer__inner">
        <div className="site-footer__brand">
          <Brand />
          <p>Travel form assistance, on your terms.</p>
        </div>
        <div className="site-footer__links">
          <div>
            <span>Product</span>
            <a href="/#workflow">How it works</a>
            <a href="/#product">What it fills</a>
            <a href="/#countries">Supported countries</a>
            <a href="/#availability">Download Entrfill</a>
            <a href="/#safety">Safety boundary</a>
          </div>
          <div>
            <span>Company</span>
            <a href="/privacy.html">Privacy</a>
            <a href="/terms.html">Terms</a>
            <a href="/disclaimer.html">Disclaimer</a>
            <a href="/support.html">Support</a>
            <a href="/delete-account.html">Delete account</a>
          </div>
        </div>
      </div>
      <div className="site-footer__bottom">
        <span>© 2026 Entrfill. All rights reserved.</span>
        <span>Not a government, embassy, airline, or visa-processing service.</span>
      </div>
    </footer>
  );
}

function SectionHeading({
  eyebrow,
  title,
  children,
}: {
  eyebrow: string;
  title: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="section-heading">
      <p className="eyebrow">{eyebrow}</p>
      <h2>{title}</h2>
      {children ? <p>{children}</p> : null}
    </div>
  );
}

function ProductPreview() {
  return (
    <div className="product-preview" aria-label="Entrfill product preview">
      <div className="product-preview__top">
        <span className="preview-signal" />
        <span>ENTRFILL</span>
        <CircleHelp aria-hidden="true" size={16} />
      </div>
      <div className="product-preview__content">
        <div className="preview-welcome">
          <p>ENTRY FORM ASSISTANCE</p>
          <h3>Singapore entry form</h3>
          <span>Ready to review and fill</span>
        </div>
        <div className="preview-progress">
          <div>
            <span>1</span>
            <p>Extract</p>
            <Check aria-hidden="true" size={14} />
          </div>
          <i />
          <div>
            <span>2</span>
            <p>Review</p>
            <Check aria-hidden="true" size={14} />
          </div>
          <i />
          <div className="preview-progress__active">
            <span>3</span>
            <p>Fill</p>
          </div>
        </div>
        <div className="preview-selection">
          <div className="preview-selection__label">
            <span>TRAVELER DATA</span>
            <Check aria-hidden="true" size={14} />
          </div>
          <strong>Passport details organized</strong>
          <small>Name, nationality, and document dates</small>
        </div>
        <div className="preview-selection">
          <div className="preview-selection__label">
            <span>TRIP + STAY</span>
            <Check aria-hidden="true" size={14} />
          </div>
          <strong>Journey details organized</strong>
          <small>Flight and accommodation selected</small>
        </div>
        <div className="preview-result">
          <span>
            <Check aria-hidden="true" size={14} />
            Supported fields ready
          </span>
          <small>Manual items stay visible for review</small>
        </div>
      </div>
      <div className="product-preview__action">
        <span>Fill supported fields</span>
        <ArrowRight aria-hidden="true" size={16} />
      </div>
    </div>
  );
}

export function HomePage() {
  return (
    <>
      <SiteHeader isHome />
      <main>
        <section className="hero">
          <div className="hero__grid">
            <div className="hero__copy">
              <p className="eyebrow eyebrow--light">TRAVEL FORM ASSISTANCE</p>
              <h1>Simplify your entry forms.</h1>
              <p className="hero__lede">
                Entrfill helps you turn passports, itineraries, and stay details into reviewed travel
                data, then quickly fills supported fields in visa applications and arrival cards when you
                choose.
              </p>
              <div className="hero__actions">
                <div className="hero__store-actions">
                  <a
                    className="button button--gold"
                    href={appleAppStoreUrl}
                    rel="noreferrer"
                    target="_blank"
                  >
                    <Apple aria-hidden="true" fill="currentColor" size={17} strokeWidth={1.8} />
                    Download on the App Store
                  </a>
                  <a
                    className="button button--store-outline"
                    href={googlePlayUrl}
                    rel="noreferrer"
                    target="_blank"
                  >
                    <Play aria-hidden="true" fill="currentColor" size={16} strokeWidth={1.8} />
                    Download on Google Play
                  </a>
                </div>
                <a className="text-link text-link--light" href="#how-it-works">
                  See how Entrfill works
                  <ArrowRight aria-hidden="true" size={15} />
                </a>
              </div>
              <p className="hero__store-note">Available now on the App Store and Google Play.</p>
              <div className="hero__notes">
                <span>
                  <Check aria-hidden="true" size={15} />
                  Details organized in one place
                </span>
                <span>
                  <Check aria-hidden="true" size={15} />
                  You review before filling
                </span>
              </div>
            </div>
            <div className="hero__visual">
              <div className="hero__passport">
                <span>TRAVEL DOCUMENT</span>
                <strong>Prepared</strong>
                <small>For your next trip</small>
              </div>
              <ProductPreview />
              <div className="hero__globe" aria-hidden="true">
                <Globe2 size={42} strokeWidth={1.15} />
              </div>
            </div>
          </div>
        </section>

        <section className="section section--quiet" id="how-it-works">
          <div className="content-grid content-grid--features">
            <SectionHeading eyebrow="WHY ENTRFILL" title="Skip the repetitive typing. Keep the important decisions.">
              Entrfill turns the travel information you already have into a reusable, reviewable source for
              the entry forms your trip requires.
            </SectionHeading>
            <div className="feature-list">
              <article>
                <span className="feature-icon feature-icon--red">
                  <FileCheck2 aria-hidden="true" size={22} strokeWidth={1.7} />
                </span>
                <div>
                  <h3>Bring your documents</h3>
                  <p>Add passport, itinerary, and accommodation details in one place for the trip you are planning.</p>
                </div>
              </article>
              <article>
                <span className="feature-icon feature-icon--gold">
                  <Sparkles aria-hidden="true" size={22} strokeWidth={1.7} />
                </span>
                <div>
                  <h3>Organize details for reuse</h3>
                  <p>Entrfill helps gather key travel information into reusable fields you can review.</p>
                </div>
              </article>
              <article>
                <span className="feature-icon feature-icon--green">
                  <FileCheck2 aria-hidden="true" size={22} strokeWidth={1.7} />
                </span>
                <div>
                  <h3>Reuse what you confirmed</h3>
                  <p>Keep one checked traveler profile ready for the visa and arrival forms your trip requires.</p>
                </div>
              </article>
              <article>
                <span className="feature-icon feature-icon--green">
                  <ShieldCheck aria-hidden="true" size={22} strokeWidth={1.7} />
                </span>
                <div>
                  <h3>Start fast autofill with a click</h3>
                  <p>On a supported official page, trigger autofill to complete as many safe, visible fields as Entrfill can.</p>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section className="section section--workflow" id="workflow">
          <div className="workflow-layout">
            <SectionHeading eyebrow="HOW IT WORKS" title="One trip in. Less retyping out.">
              The useful part is the handoff: scattered travel documents become confirmed information that
              can move into the next supported entry form.
            </SectionHeading>
            <ol className="workflow-list">
              <li>
                <span>01</span>
                <div>
                  <strong>Share your travel details</strong>
                  <p>Start with the passport, transport, and stay information you already need for the trip.</p>
                </div>
              </li>
              <li>
                <span>02</span>
                <div>
                  <strong>Organize into reusable data</strong>
                  <p>Entrfill helps gather useful fields into structured travel data for reuse.</p>
                </div>
              </li>
              <li>
                <span>03</span>
                <div>
                  <strong>You review and confirm</strong>
                  <p>Check the details before they become part of an autofill run. Nothing important is silently accepted.</p>
                </div>
              </li>
              <li>
                <span>04</span>
                <div>
                  <strong>Fill the current official form</strong>
                  <p>Choose when to start. Entrfill fills supported visible fields and shows what still needs your attention.</p>
                </div>
              </li>
            </ol>
          </div>
        </section>

        <section className="section section--preview" id="product">
          <div className="preview-layout">
            <div>
              <SectionHeading eyebrow="WHAT ENTRFILL FILLS" title="Built for the forms that slow down a trip.">
                Entrfill is designed for visa applications and arrival cards. Current live coverage starts
                with supported arrival-card flows, while official requirements and field availability can change.
              </SectionHeading>
              <div className="scope-list">
                <div>
                  <strong>Visa applications</strong>
                  <span>Reuse confirmed identity and travel details across supported application forms.</span>
                </div>
                <div>
                  <strong>Arrival cards</strong>
                  <span>Fill currently visible, eligible fields on supported official destination pages.</span>
                </div>
                <div>
                  <strong>Country-aware mapping</strong>
                  <span>Adapt the same travel data to the field language and structure of each supported form.</span>
                </div>
                <div>
                  <strong>Clear results</strong>
                  <span>See what was filled and which questions or actions still need you.</span>
                </div>
              </div>
              <a className="text-link" href="/disclaimer.html">
                Read the safety boundary
                <ArrowRight aria-hidden="true" size={15} />
              </a>
            </div>
            <div className="preview-layout__showcase">
              <ProductPreview />
            </div>
          </div>
        </section>

        <section className="section" id="countries">
          <div className="countries-intro">
            <SectionHeading eyebrow="CURRENT COVERAGE" title="Start with the routes travelers repeat most.">
              Entrfill is built for visa applications and arrival cards. The destinations below are the
              current supported arrival-card coverage; availability can change when an official form changes.
            </SectionHeading>
          </div>
          <div className="country-list">
            {supportedCountries.map((country) => (
              <div className="country-row" key={country.code}>
                <span>{country.code}</span>
                <p>{country.name}</p>
                <ChevronDown aria-hidden="true" size={17} />
              </div>
            ))}
          </div>
        </section>

        <section className="section section--safety" id="safety">
          <div className="safety-layout">
            <SectionHeading eyebrow="SAFETY IS A PRODUCT DECISION" title="Less repetition. You keep judgment.">
              Entrfill speeds up information entry without taking ownership of an official decision.
            </SectionHeading>
            <div className="safety-checklist">
              <p>
                <Check aria-hidden="true" size={17} />
                You start every fill.
              </p>
              <p>
                <Check aria-hidden="true" size={17} />
                Entrfill stops before final submission.
              </p>
              <p>
                <Check aria-hidden="true" size={17} />
                You submit every form.
              </p>
              <p>
                <Check aria-hidden="true" size={17} />
                You confirm declarations, documents, and every official answer.
              </p>
              <p>
                <Check aria-hidden="true" size={17} />
                You handle CAPTCHAs, uploads, and official page choices.
              </p>
            </div>
          </div>
        </section>

        <section className="availability" id="availability">
          <div className="availability__copy">
            <p className="eyebrow">DOWNLOAD ENTRFILL</p>
            <h2>Make your next entry form easier.</h2>
            <p>
              Entrfill is available now for iPhone on the App Store and for Android on Google Play.
            </p>
          </div>
          <div className="store-downloads">
            <a
              className="store-download store-download--live"
              href={appleAppStoreUrl}
              rel="noreferrer"
              target="_blank"
            >
              <Apple aria-hidden="true" fill="currentColor" size={27} strokeWidth={1.7} />
              <span>
                <small>DOWNLOAD ON THE</small>
                <strong>App Store</strong>
              </span>
              <ExternalLink aria-hidden="true" className="store-download__status" size={17} />
            </a>
            <a
              className="store-download store-download--live"
              href={googlePlayUrl}
              rel="noreferrer"
              target="_blank"
            >
              <Play aria-hidden="true" fill="currentColor" size={27} strokeWidth={1.7} />
              <span>
                <small>GET IT ON</small>
                <strong>Google Play</strong>
              </span>
              <ExternalLink aria-hidden="true" className="store-download__status" size={17} />
            </a>
          </div>
        </section>
      </main>
      <div className="mobile-store-dock" aria-label="Entrfill app availability">
        <a
          aria-label="Download Entrfill on the App Store"
          href={appleAppStoreUrl}
          rel="noreferrer"
          target="_blank"
        >
          <Apple aria-hidden="true" fill="currentColor" size={18} strokeWidth={1.8} />
          <span>
            <small>DOWNLOAD ON THE</small>
            <strong>App Store</strong>
          </span>
        </a>
        <a
          aria-label="Download Entrfill on Google Play"
          href={googlePlayUrl}
          rel="noreferrer"
          target="_blank"
        >
          <Play aria-hidden="true" fill="currentColor" size={18} strokeWidth={1.8} />
          <span>
            <small>GET IT ON</small>
            <strong>Google Play</strong>
          </span>
        </a>
      </div>
      <SiteFooter />
    </>
  );
}

function LegalPage({ page }: { page: LegalPageName }) {
  const content = legalContent[page];
  return (
    <>
      <SiteHeader />
      <main className="legal-page">
        <article>
          <p className="eyebrow">{content.eyebrow}</p>
          <h1>{content.title}</h1>
          <p className="legal-page__intro">{content.intro}</p>
          <p className="legal-page__date">Last updated: August 3, 2026</p>
          <div className="legal-page__sections">
            {content.sections.map((section) => (
              <section key={section.title}>
                <h2>{section.title}</h2>
                <p>{section.body}</p>
              </section>
            ))}
          </div>
        </article>
      </main>
      <SiteFooter />
    </>
  );
}

export function PrivacyPage() {
  return <LegalPage page="privacy" />;
}

export function TermsPage() {
  return <LegalPage page="terms" />;
}

export function DisclaimerPage() {
  return <LegalPage page="disclaimer" />;
}

const deletionRequestEmail =
  'mailto:9tonsg@gmail.com?subject=Entrfill%20account%20deletion%20request&body=Sign-in%20provider%20(Google%20or%20Apple)%3A%20%0ASign-in%20email%3A%20%0AEntrfill%20Account%20ID%3A%20%0A%0AI%20request%20permanent%20deletion%20of%20this%20Entrfill%20account%20and%20its%20associated%20data.%0A%0APlease%20do%20not%20include%20passport%20numbers%2C%20document%20images%2C%20passwords%2C%20payment%20details%2C%20or%20verification%20codes.';

export function DeleteAccountPage() {
  return (
    <>
      <SiteHeader />
      <main className="deletion-page">
        <header className="deletion-page__hero">
          <div>
            <p className="eyebrow eyebrow--light">ACCOUNT &amp; DATA</p>
            <h1>Delete your Entrfill account.</h1>
            <p>
              You can permanently delete your account in Entrfill or request deletion by email if
              you no longer have access to the app.
            </p>
          </div>
          <a className="deletion-page__request" href={deletionRequestEmail}>
            <Mail aria-hidden="true" size={18} strokeWidth={1.8} />
            Request account deletion
          </a>
        </header>

        <div className="deletion-page__content">
          <section className="deletion-method">
            <div className="deletion-method__heading">
              <span className="feature-icon feature-icon--green">
                <Trash2 aria-hidden="true" size={22} strokeWidth={1.7} />
              </span>
              <div>
                <p className="eyebrow">FASTEST METHOD</p>
                <h2>Delete your account in the app</h2>
              </div>
            </div>
            <ol className="deletion-steps">
              <li>
                <span>1</span>
                <p>Open Entrfill and sign in to the account you want to delete.</p>
              </li>
              <li>
                <span>2</span>
                <p>
                  Open <strong>Profile</strong>, tap your account card, then choose{' '}
                  <strong>Delete account</strong>.
                </p>
              </li>
              <li>
                <span>3</span>
                <p>Review the deletion notice and tap <strong>Delete permanently</strong>.</p>
              </li>
            </ol>
            <p className="deletion-method__note">
              In-app deletion takes effect after Entrfill confirms the request. It cannot be undone.
            </p>
          </section>

          <section className="deletion-method">
            <div className="deletion-method__heading">
              <span className="feature-icon feature-icon--gold">
                <Mail aria-hidden="true" size={22} strokeWidth={1.7} />
              </span>
              <div>
                <p className="eyebrow">NO APP ACCESS NEEDED</p>
                <h2>Send us a deletion request</h2>
              </div>
            </div>
            <p>
              Email{' '}
              <a className="inline-link" href={deletionRequestEmail}>
                9tonsg@gmail.com
              </a>{' '}
              from the address linked to your Entrfill account. Use the subject{' '}
              <strong>Entrfill account deletion request</strong> and include your sign-in provider,
              sign-in email, and Entrfill Account ID.
            </p>
            <p>
              After locating the account, we send a confirmation to the email stored on that
              account. Deletion proceeds only after the account holder completes verification.
            </p>
            <a className="button deletion-method__button" href={deletionRequestEmail}>
              <Mail aria-hidden="true" size={16} strokeWidth={1.8} />
              Start email request
            </a>
            <p className="deletion-method__warning">
              Do not email passport numbers, document images, passwords, payment details, or
              verification codes.
            </p>
          </section>

          <section className="deletion-identity" aria-labelledby="deletion-identity-title">
            <div className="deletion-identity__heading">
              <span className="feature-icon feature-icon--green">
                <ShieldCheck aria-hidden="true" size={22} strokeWidth={1.7} />
              </span>
              <div>
                <p className="eyebrow">ACCOUNT VERIFICATION</p>
                <h2 id="deletion-identity-title">How we identify the account to delete</h2>
              </div>
            </div>
            <div className="deletion-identity__content">
              <p>
                You do not need to sign in with Google on this webpage. Entrfill supports both
                Google and Apple sign-in, so a Google-only web flow would not cover every account.
              </p>
              <ol>
                <li>
                  Send the request from the email linked to your Entrfill account whenever possible.
                </li>
                <li>
                  Include your sign-in provider, sign-in email, and the Entrfill Account ID shown
                  on your <strong>Profile</strong> account card.
                </li>
                <li>
                  If you used Sign in with Apple and chose Hide My Email, provide the Apple relay
                  email shown for Entrfill. We can send the verification message to that relay
                  address, which Apple forwards to you.
                </li>
                <li>
                  We match the Account ID to the sign-in binding and may ask for additional
                  verification. We do not delete an account when the information is missing,
                  ambiguous, or does not match.
                </li>
              </ol>
            </div>
          </section>

          <section className="deletion-data" aria-labelledby="deletion-data-title">
            <div>
              <p className="eyebrow">WHAT HAPPENS</p>
              <h2 id="deletion-data-title">Account data is removed with the account.</h2>
              <p>
                Your active sessions end and the account cannot be recovered after deletion.
                Deleting Entrfill does not cancel a Google Play or App Store subscription; manage
                any recurring subscription in the store where you purchased it.
              </p>
            </div>
            <div className="deletion-data__lists">
              <article>
                <h3>Deleted or de-identified</h3>
                <ul>
                  <li>Account profile and sign-in bindings</li>
                  <li>Traveler and passport records</li>
                  <li>Uploaded documents and recognition results</li>
                  <li>Itinerary and accommodation information</li>
                  <li>Arrival-card autofill usage and assistance records</li>
                </ul>
              </article>
              <article>
                <h3>Limited records we may retain</h3>
                <ul>
                  <li>Payment, refund, subscription, and transaction records</li>
                  <li>Security, fraud-prevention, regulatory, and audit records</li>
                </ul>
                <p>
                  These records are retained only when required for legal, financial, security, or
                  regulatory obligations, for the applicable required period, and are kept
                  de-identified from your deleted profile where possible.
                </p>
              </article>
            </div>
          </section>

          <section className="deletion-developer" aria-label="Developer contact">
            <span>Developer contact</span>
            <a href="mailto:9tonsg@gmail.com">9tonsg@gmail.com</a>
          </section>
        </div>
      </main>
      <SiteFooter />
    </>
  );
}

export function SupportPage() {
  return (
    <>
      <SiteHeader />
      <main className="support-page">
        <div className="support-page__heading">
          <p className="eyebrow">SUPPORT</p>
          <h1>Help with Entrfill.</h1>
          <p>
            Get help with your account, travel data, saved details, and supported form filling.
            Official decisions, declarations, and submissions remain yours.
          </p>
        </div>
        <section className="support-list">
          <article>
            <span className="feature-icon feature-icon--gold">
              <CircleHelp aria-hidden="true" size={22} strokeWidth={1.7} />
            </span>
            <div>
              <h2>Get product help</h2>
              <p>Open Profile, then Contact us in Entrfill to describe an issue, share a support request, or ask for help with your account.</p>
            </div>
          </article>
          <article>
            <span className="feature-icon feature-icon--green">
              <ShieldCheck aria-hidden="true" size={22} strokeWidth={1.7} />
            </span>
            <div>
              <h2>Manage your information</h2>
              <p>
                Review, correct, or remove travel information directly in Entrfill. To permanently
                delete your Entrfill account and associated data, use our{' '}
                <a className="inline-link" href="/delete-account.html">
                  account deletion page
                </a>
                .
              </p>
            </div>
          </article>
          <article>
            <span className="feature-icon feature-icon--red">
              <ExternalLink aria-hidden="true" size={22} strokeWidth={1.7} />
            </span>
            <div>
              <h2>Need official guidance?</h2>
              <p>Use the relevant government, embassy, airline, or destination authority website for entry rules, form requirements, and travel decisions.</p>
            </div>
          </article>
        </section>
      </main>
      <SiteFooter />
    </>
  );
}
